import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    IconButton
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import DownloadIcon from '@mui/icons-material/GetApp';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TeamOnLeave = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState([]);
    const [year, setYear] = useState(new Date().getFullYear());
    const [managerId, setManagerId] = useState('');
    const [companyId, setCompanyId] = useState('');

    const COLORS = [
        '#4ecdc4', // Turquoise
        '#ff6b6b', // Pastel Red
        '#ffe66d', // Yellow
        '#a6e3e9', // Light Blue
        '#ff9ff3', // Pink
        '#feca57', // Orange
        '#54a0ff', // Blue
        '#5f27cd', // Purple
    ];

    useEffect(() => {
        const fetchUserAndData = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setError("No authentication token found. Please log in.");
                    setLoading(false);
                    return;
                }

                const payload = JSON.parse(atob(token.split('.')[1]));
                const compId = payload.company_id;
                setCompanyId(compId);

                let mgrId = payload.employee_id;

                if (!mgrId) {
                    try {
                        const userRes = await axios.get(`${API_URL}/api/user/current?company_id=${compId}`, {
                            withCredentials: true,
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        if (userRes.data && userRes.data.employee_id) {
                            mgrId = userRes.data.employee_id;
                        }
                    } catch (e) {
                        //console.error("Error fetching user info:", e);
                    }
                }

                if (mgrId) {
                    setManagerId(mgrId);
                    fetchAnalytics(mgrId, compId, year);
                } else {
                    setError("User information not found. Please re-login.");
                    setLoading(false);
                }
            } catch (err) {
                setError("Failed to fetch user information");
                setLoading(false);
            }
        };

        fetchUserAndData();
    }, [year]);

    const fetchAnalytics = async (mgrId, compId, targetYear) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_URL}/api/leaves/analytics/${mgrId}?year=${targetYear}`,
                {   headers: {
                    Authorization: `Bearer ${token}`
                  } }
            );
            setData(response.data);
            setError('');
        } catch (err) {
            setError("Failed to fetch leave analytics");
        } finally {
            setLoading(false);
        }
    };

    const getLeaveTypes = () => {
        const types = new Set();
        data.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== 'name' && typeof item[key] === 'number') types.add(key);
            });
        });
        return Array.from(types);
    };

    const leaveTypes = getLeaveTypes();

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Paper sx={{ p: 2, boxShadow: 3, border: '1px solid #eee', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#333' }}>{label}</Typography>
                    {payload.map((entry, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
                            <Box sx={{ width: 12, height: 12, bgcolor: entry.fill, borderRadius: '2px' }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                                {entry.name}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {entry.value}
                            </Typography>
                        </Box>
                    ))}
                </Paper>
            );
        }
        return null;
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4, bgcolor: '#fdfdfd', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1.5 }}>

                    Team On Leave
                </Typography>

            </Box>

            <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={3}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>Duration</Typography>
                    <FormControl fullWidth variant="outlined" size="small">
                        <Select
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            sx={{ bgcolor: 'white', borderRadius: 1.5 }}
                        >
                            {[2024, 2025, 2026].map(y => (
                                <MenuItem key={y} value={y}>{y === new Date().getFullYear() ? `This Year (${y})` : y === new Date().getFullYear() - 1 ? `Last Year (${y})` : y}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.03)', border: '1px solid #f0f0f0' }}>
                <Typography variant="subtitle2" sx={{ mb: 4, fontWeight: 700, color: '#34495e', letterSpacing: 0.5 }}>
                    Duration Selected : 01 Jan {year} TO 31 Dec {year}
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 15, gap: 2 }}>
                        <CircularProgress size={40} thickness={4} sx={{ color: '#00bcd4' }} />
                        <Typography variant="body2" color="text.secondary">Fetching team leave data...</Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
                ) : (
                    <Box sx={{ height: 450, width: '100%', mt: 2 }}>
                        <ResponsiveContainer>
                            <BarChart
                                layout="vertical"
                                data={data}
                                margin={{ top: 10, right: 30, left: 40, bottom: 20 }}
                                barGap={0}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                                <XAxis
                                    type="number"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#95a5a6', fontSize: 13, fontWeight: 500 }}
                                />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#95a5a6', fontSize: 13, fontWeight: 500 }}
                                    width={100}
                                />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8f9fa' }} />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    iconType="circle"
                                    iconSize={10}
                                    wrapperStyle={{ paddingTop: '20px' }}
                                    formatter={(value) => <span style={{ color: '#7f8c8d', fontSize: 13, fontWeight: 500, marginLeft: 5 }}>{value}</span>}
                                />
                                {leaveTypes.map((t, index) => (
                                    <Bar
                                        key={t}
                                        dataKey={t}
                                        stackId="a"
                                        fill={COLORS[index % COLORS.length]}
                                        barSize={20}
                                        radius={index === leaveTypes.length - 1 ? [0, 4, 4, 0] : [0, 0, 0, 0]}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                )}
            </Paper>

        </Container>
    );
};

export default TeamOnLeave;
